import {
  OfferCard,
  OfferCardProps,
  OfferGiftCard,
} from '@cash-rewards/cr-ui-components';
import { useGetFlag } from '@cash-rewards/optimizely';
import { clsxm, getOfferUrl } from '@cash-rewards/reactjs';
import { useMemo } from 'react';

import { featureToggle } from '@/constants/FeatureToggles';
import { OFFER_BADGE_MAP } from '@/modules/core/constants/badgeType';
import { getSubComissionString } from '@/modules/core/utils/offers';
import useFavouriteStore from '@/modules/shop/hooks/useFavouriteStore';
import { useOfferAsterisk } from '@/modules/shop/hooks/useOfferAsterisk';
import { OfferModel } from '@/modules/shop/models/offerModels';

interface OfferCarouselCardProps {
  offer: OfferModel;
  className?: string;
  imageLoading?: OfferCardProps['imageLoading'];
  hideDescription?: boolean;
}
const OfferCarouselCard = ({
  offer,
  className,
  hideDescription,
  ...rest
}: OfferCarouselCardProps) => {
  const { isFavourite, updateFavouriteStore } = useFavouriteStore(
    offer.merchantId,
    offer
  );

  const { enabled, variationKey } = useGetFlag(featureToggle.OffersCashbackCap);
  const { commissionString, showAsterisk } = useOfferAsterisk(
    offer.cashbackCapDescription,
    offer.merchant.firstTierMinimumSpendString,
    offer.clientCommissionString
  );

  const showCashbackCap = enabled && variationKey === 'on' && !showAsterisk;

  const isGiftCard = offer.merchant.isEonxGiftCard;
  const Comp = isGiftCard ? OfferGiftCard : OfferCard;

  let badgeType;
  if (isGiftCard) {
    badgeType = OFFER_BADGE_MAP['GiftCard'];
  } else if (offer.offerBadge) {
    badgeType = OFFER_BADGE_MAP[offer.offerBadge];
  }

  const link = useMemo(() => {
    return getOfferUrl(offer);
  }, [offer]);

  const subComissionString = getSubComissionString(offer, showCashbackCap);

  return (
    <Comp
      className={clsxm('w-[256px] ', className)}
      imageUrl={
        !isGiftCard ? offer.offerBackgroundImageUrl : offer.merchantLogoUrl
      }
      logoUrl={!isGiftCard ? offer.merchantLogoUrl : undefined}
      title={offer.merchant.name}
      description={!hideDescription ? offer.title : undefined}
      commissionString={commissionString}
      subComissionString={subComissionString}
      endDate={offer.endDateTimeOffset}
      aspectRatio='1:1'
      href={link}
      isFavourite={isFavourite}
      onFavouriteChange={updateFavouriteStore}
      badgeType={badgeType}
      cardImageAs='img'
      {...rest}
    ></Comp>
  );
};

export default OfferCarouselCard;
